import React,{useState,useEffect} from "react";
import { refreshNotifications } from "../utils/notifications";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Grid, Tooltip, ButtonBase
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import ListParty from "./listParty";
import CopyValue from "./copyValue";
import { formatUsPhone } from "../utils/contacts";
import { withRouter } from "../utils/router";
import { makeStyles } from 'tss-react/mui';
import EnhancedTable from "./enhancedTable";
import {
  deleteConference,
  getConferences,
  updateConference,
} from "../utils/utils";
import {
  setNotifications,
  setNotificationsLoading,
} from "../store/features/notification/notificationSlice";
import { setCalls } from "../store/features/calls/callSlice";
import { setVoicemails } from "../store/features/voicemails/voicemailSlice";
import { setSmsConversations } from "../store/features/smsConversations/smsConversationsSlice";
import { setFaxes } from "../store/features/faxes/faxSlice";
import { setRecordings } from "../store/features/recordings/recordingSlice";
import {
  setConferences,
} from "../store/features/conferences/conferenceSlice";
import { setError } from "../store/features/error/errorSlice";
import CreateConference from "./createConference";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { resetSession } from "../store/features/session/sessionSlice";
import EditTitle from "./editTitle";

const mapDispatchToProps = {
  setError,
  setCalls,
  setVoicemails,
  setSmsConversations,
  setFaxes,
  setRecordings,
  setNotifications,
  setNotificationsLoading,
  setConferences,
  resetSession,
};

const useStyles = makeStyles()((theme) => ({
  // Access of a conference: a soft pill with a lock, a button for the owner.
  access: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px 3px 8px",
    borderRadius: 12,
    fontFamily: "inherit",
    fontSize: 12.5,
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    "& svg": {
      fontSize: 15,
    },
  },
  accessPrivate: {
    color: "#c2185b",
    backgroundColor: "rgba(233, 30, 99, 0.10)",
  },
  accessPublic: {
    color: "#1f8f4a",
    backgroundColor: "rgba(46, 158, 85, 0.12)",
  },
  accessButton: {
    transition: "filter 0.15s ease",
    "&:hover": {
      filter: "brightness(0.94)",
    },
  },
}));

const headCells = {
  personal: [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Conference",
    },
    { id: "adminPin", numeric: true, disablePadding: false, label: "Admin PIN" },
    { id: "guestPin", numeric: true, disablePadding: false, label: "Guest PIN" },
    { id: "type", numeric: true, disablePadding: false, label: "Access" },
    { id: "actions", numeric: true, disablePadding: false, label: "Actions" },
  ],
  public: [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Conference",
    },
    { id: "guestPin", numeric: true, disablePadding: false, label: "Guest PIN" },
    { id: "owner", numeric: true, disablePadding: false, label: "Owner" },
    { id: "actions", numeric: true, disablePadding: false, label: "Actions" },
  ],
};


const ConferenceTable = (props) => {
  const {
    tab,
    title,
    menu,
    menuItems,
    history,
    conferences,
    session,
    resetSession,
    setVoicemails,
    setSmsConversations,
    setFaxes,
    setRecordings,
    setNotifications,
    setNotificationsLoading,
    notifications,
    setConferences,
    setValue,
    setError,
    handleDialClick,
  } = props;

  const { classes, cx } = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [conferenceModal, setConferenceModal] = useState(false);
  const [titleChangeItem, setTitleChangeItem] = useState(null);
  const [titleModal, setTitleModal] = useState(false)

  const createConferenceCb = () => {
    setConferenceModal(true)
  }

  const defaultValues = {
    server: session.lastServer,
    token: session.token,
  };

  // Private or public. The owner can switch it by clicking the pill.
  const getLockStatus = (item) => {
    const isPrivate = String(item.type || "").toLowerCase() === "private";
    const isOwner = String(item.owner || "").toLowerCase() === "yes";
    const content = (
      <>
        {isPrivate ? <LockIcon /> : <LockOpenIcon />}
        {isPrivate ? "Private" : "Public"}
      </>
    );
    const tone = isPrivate ? classes.accessPrivate : classes.accessPublic;

    if (!isOwner) {
      return <span className={cx(classes.access, tone)}>{content}</span>;
    }
    return (
      <Tooltip title={isPrivate ? "Make public" : "Make private"}>
        <ButtonBase
          aria-label={isPrivate ? "private conference, make public" : "public conference, make private"}
          className={cx(classes.access, classes.accessButton, tone)}
          onClick={() => handleStatusChange(isPrivate ? "public" : "private", item.cid)}
        >
          {content}
        </ButtonBase>
      </Tooltip>
    );
  };

  // The room, with its dial-in number underneath when it has one.
  const renderName = (item) => (
    <ListParty
      icon={<GroupsIcon />}
      tone="brand"
      title={item.name || "Conference"}
      detail={item.trunk ? `Dial in ${formatUsPhone(item.trunk)}` : ""}
      wrap
    />
  );

  // Search: the conference name, its owner, a PIN, or the dial-in number
  // typed any way.
  const term = searchTerm.trim().toLowerCase();
  const termDigits = /^[\d\s()+.-]+$/.test(term) ? term.replace(/\D/g, "") : "";
  const matchesSearch = (item) => {
    if (term.length < 2) return true;
    if (String(item.name || "").toLowerCase().includes(term)) return true;
    if (String(item.ownerName || "").toLowerCase().includes(term)) return true;
    if (termDigits.length < 2) return false;
    return [item.adminPin, item.userPin, item.trunk].some((value) => {
      const digits = String(value || "").replace(/\D/g, "");
      return digits !== "" && (digits.includes(termDigits) ||
        (termDigits.length > 2 && termDigits[0] === "1" && digits.includes(termDigits.slice(1))));
    });
  };

  // Each tab starts on its first page.
  useEffect(() => {
    setPage(0);
  }, [tab]);

  const generateRows = () => {
    switch (tab) {
      case "personal":
        return conferences
          .filter((item) => item.owner.toLowerCase() === "yes" && matchesSearch(item))
          .map((item) => {
            return {
              name: renderName(item),
              adminPin: <CopyValue value={item.adminPin} label="admin PIN" />,
              guestPin: <CopyValue value={item.userPin} label="guest PIN" />,
              type: getLockStatus(item),
              actions: [
                { type: "editTitle" },
                { type: "shareConference" },
                { type: "dialConference" },
                { type: "deleteConference" },
              ],
              object: item,
            };
          });
      case "public":
        return conferences
          .filter((item) => item.owner.toLowerCase() === "no" && matchesSearch(item))
          .map((item) => {
            return {
              name: renderName(item),
              guestPin: <CopyValue value={item.userPin} label="guest PIN" />,
              owner: item.ownerName,
              actions: [{ type: "dialConference" }],
              object: item,
            };
          });
      default:
        break;
    }
  };

  const refetchNotifications = () => refreshNotifications({
    values: defaultValues,
    setters: { setCalls, setFaxes, setRecordings, setVoicemails, setSmsConversations },
    extra: [getConferences(defaultValues, setConferences)],
    setNotifications,
    setNotificationsLoading,
    onUnauthorized: () => {
      resetSession();
      history.push('/login');
    },
  });

  useEffect(() => {
    refetchNotifications();
    // eslint-disable-next-line
  }, [tab]);

  const handleStatusChange = async (type,id) => {
    let conference = conferences.find(item=>item.cid===id);
    console.log('conferences handle status',conferences,conference)
    let values = defaultValues
    values.conference ={
      name: conference.name,
      type: type
    }
    try {
      await updateConference(
        values,
        id
      );
      refetchNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = (items) => {
    let values = defaultValues;

    values.conferences = items.map((item) =>
      item.hasOwnProperty("object")
        ? {
            cid: item.object.cid,
            title: item.object.title,
            viewed: "yes",
            type: "deleted",
          }
        : {
            cid: item.cid,
            title: item.title,
            viewed: "yes",
            type: "deleted",
          }
    );

    try {
      updateConference(values);
      refetchNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteForever = (item) => {
    let values = defaultValues;
    values.id = item.cid;

    try {
      deleteConference(values);
      setError({
        visible: true,
        type: "success",
        message: "Successfully deleted Item",
      });
      refetchNotifications();
    } catch (error) {
      setError({
        visible: true,
        type: "error",
        message: error,
      });
      console.error(error);
    }
  };

  return (
    <Grid container>
      {conferenceModal && (
        <CreateConference
          open={conferenceModal}
          setOpen={setConferenceModal}
          defaultValues={defaultValues}
          reloadNotifications={refetchNotifications}
        />
      )}

      {titleModal && (
        <EditTitle
          open={titleModal}
          setOpen={setTitleModal}
          value={titleChangeItem}
          type="Conference"
          defaultValues={defaultValues}
          setTitleChangeItem={setTitleChangeItem}
          reloadNotifications={refetchNotifications}
        />
      )}

      <Grid size={12}>
        <EnhancedTable
          headerData={headCells}
          rowData={generateRows()}
          hasActions={true}
          selectable={false}
          menu={menu ? menuItems : false}
          searchFunc={(value) => { setSearchTerm(value); setPage(0); }}
          title={title}
          tab={tab}
          setError={setError}
          type="conference"
          dense={false}
          history={history}
          hasClear={false}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          page={page}
          setValue={setValue}
          loading={notifications.loading}
          statusChangeFunction={handleStatusChange}
          handleDelete={handleDelete}
          handleDeleteForever={handleDeleteForever}
          setTitleChangeItem={setTitleChangeItem}
          setEditTitle={setTitleModal}
          createItemCallback={createConferenceCb}
          createItemTooltip={"Create Conference"}
          handleDialClick={handleDialClick}
          useGroupFilter={false}
        />
      </Grid>
    </Grid>
  );
};

ConferenceTable.propTypes = {
  title: PropTypes.string.isRequired,
  menu: PropTypes.bool.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      linkId: PropTypes.string,
    })
  ),
  tab: PropTypes.string.isRequired,
  handleDialClick: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    session: state.session,
    conferences: state.conferences,
    notifications: state.notifications,
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ConferenceTable)
);